import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  Box,
  Container,
  Heading,
  Image,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import DynamicEventsSection from '../components/landing/DynamicEventsSection';
import DynamicPropostePubblicheSection from '../components/landing/DynamicPropostePubblicheSection';
import LandingCategoryIdeasSection from '../components/landing/LandingCategoryIdeasSection';
import LandingNeedsSection from '../components/landing/LandingNeedsSection';
import landingPagesConfig from '../config/landingPagesConfig';
import {
  getThemeFontImport,
  resolveProposalTheme,
  resolveProposalThemeKeyFromPath,
} from '../components/proposals-standalone/proposalThemes';
import {
  StandaloneFeatureGrid,
  StandaloneFinalCta,
  StandaloneHero,
} from '../components/proposals-standalone/StandaloneBlocks';
import homeTwoPhoto from '../images/home-2.jpg';
import laSedePhoto from '../images/la-sede.jpg';
import rinnovoTesseraBgPhoto from '../images/rinnovo-tessera-bg.jpg';
import piantalaPhoto from '../images/piantala-1.jpg';
import ce2024Photo from '../images/ce-2024.jpg';

const isExternalHref = (href) =>
  typeof href === 'string' && href.length > 0 && !href.startsWith('#');

/** Secondo bottone hero: prima azione esterna «principale» della config, poi la secondaria. */
function pickHeroSecondaryCta(a, b) {
  if (isExternalHref(a?.href)) return a;
  if (isExternalHref(b?.href)) return b;
  return null;
}

export default function TargetLandingPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+/, '');
  const landing = landingPagesConfig[slug];

  if (!landing) {
    return <Navigate to="/" replace />;
  }

  const {
    title,
    subtitle,
    heroImage,
    target,
    identitySection,
    primaryCta: landingPrimaryCta,
    secondaryCta: landingSecondaryCta,
    sections,
    dynamicContent,
  } = landing;
  const themeKey = resolveProposalThemeKeyFromPath(pathname);
  const theme = resolveProposalTheme(themeKey);
  const offers = Array.isArray(sections?.offers) ? sections.offers : [];
  const valueItems = Array.isArray(sections?.valueItems)
    ? sections.valueItems
    : [];
  const needs = Array.isArray(sections?.needs) ? sections.needs : [];
  const categories = Array.isArray(sections?.categories)
    ? sections.categories
    : [];
  const needsBasedLayout = needs.length > 0;

  const proposteCfg = dynamicContent?.propostePubbliche;
  const eventsCfg = dynamicContent?.events;
  const hasProposte = Boolean(proposteCfg?.enabled);
  const hasEvents = Boolean(eventsCfg?.enabled);

  const { heroPrimaryCta, heroSecondaryCta } = useMemo(() => {
    if (slug === 'proposte-famiglie') {
      return {
        heroPrimaryCta: {
          label: hasProposte ? 'Esplora le proposte' : 'Scopri i contenuti',
          href: hasProposte
            ? `#${proposteCfg.landing}`
            : hasEvents
              ? `#${slug}-eventi`
              : `#${slug}-contenuti`,
        },
        heroSecondaryCta: null,
      };
    }

    let inPageHref;
    let inPageLabel;

    if (hasProposte) {
      inPageHref = `#${proposteCfg.landing}`;
      inPageLabel = 'Esplora le proposte';
    } else if (hasEvents) {
      inPageHref = `#${slug}-eventi`;
      inPageLabel = 'Vedi gli eventi in pagina';
    } else {
      inPageHref = `#${slug}-contenuti`;
      inPageLabel = 'Scopri i contenuti';
    }

    return {
      heroPrimaryCta: { label: inPageLabel, href: inPageHref },
      heroSecondaryCta: pickHeroSecondaryCta(
        landingPrimaryCta,
        landingSecondaryCta
      ),
    };
  }, [
    hasProposte,
    hasEvents,
    proposteCfg,
    slug,
    landingPrimaryCta,
    landingSecondaryCta,
  ]);

  const typedEyebrow = {
    adults: 'Esperienze sociali',
    magic: 'Community competitivo-casual',
    family: 'Tempo di qualità',
    kids: 'Creatività e crescita',
    school: 'Percorsi educativi',
  }[theme.key];

  const renderMagicPhotoMoments = () => {
    if (slug !== 'magic-tcg') return null;

    const photos = [homeTwoPhoto, laSedePhoto, rinnovoTesseraBgPhoto];
    return (
      <>
        <Box
          px={4}
          py={{ base: 8, md: 10 }}
          bgImage={`linear-gradient(rgba(13,18,38,0.78), rgba(13,18,38,0.66)), url(${laSedePhoto})`}
          bgSize="cover"
          bgPosition="center"
          bgAttachment={{ base: 'scroll', md: 'fixed' }}
        >
          <Container maxW="container.xl">
            <Stack spacing={3}>
              <Text
                textTransform="uppercase"
                letterSpacing="0.08em"
                fontWeight="800"
                fontSize="sm"
                color="#93c5fd"
              >
                Atmosfera CIL
              </Text>
              <Heading
                as="h3"
                fontSize={{ base: 'xl', md: '3xl' }}
                color="white"
                fontFamily={theme.fontHeading}
              >
                Tavoli, community e momenti veri di gioco
              </Heading>
            </Stack>
          </Container>
        </Box>

        <Box bg="#0f1021" px={4} py={{ base: 10, md: 14 }}>
          <Container maxW="container.xl">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
              {photos.map((src, idx) => (
                <Box
                  key={src}
                  borderRadius="2xl"
                  overflow="hidden"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  boxShadow="0 14px 30px rgba(0,0,0,0.3)"
                  transform={{
                    base: 'none',
                    md: idx === 1 ? 'translateY(-8px)' : 'none',
                  }}
                >
                  <Image
                    src={src}
                    alt={`Momento community Magic ${idx + 1}`}
                    w="100%"
                    h={{ base: '220px', md: '260px' }}
                    objectFit="cover"
                  />
                </Box>
              ))}
            </SimpleGrid>
          </Container>
        </Box>
      </>
    );
  };

  const renderIdentitySection = () => {
    if (!identitySection) return null;
    const pills = Array.isArray(identitySection.pillars)
      ? identitySection.pillars
      : [];

    if (slug === 'magic-tcg') {
      return (
        <Box bg="#0d1226" px={4} py={{ base: 10, md: 14 }}>
          <Container maxW="container.xl">
            <Stack spacing={5}>
              <Text
                textTransform="uppercase"
                letterSpacing="0.08em"
                fontWeight="800"
                fontSize="sm"
                color="#93c5fd"
              >
                {identitySection.eyebrow}
              </Text>
              <Heading as="h2" color="white" fontFamily={theme.fontHeading}>
                {identitySection.title}
              </Heading>
              <Text color="#dbeafe" maxW="5xl">
                {identitySection.description}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {pills.map((pill) => (
                  <Box
                    key={pill}
                    bg="whiteAlpha.120"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius="xl"
                    p={4}
                  >
                    <Text color="white" fontWeight="700" fontSize="sm">
                      {pill}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
              <Text color="#bfdbfe" fontSize="sm">
                {identitySection.trustNote}
              </Text>
            </Stack>
          </Container>
        </Box>
      );
    }

    if (slug === 'eventi-adulti') {
      return (
        <Box bg="#f5f7fb" px={4} py={{ base: 10, md: 14 }}>
          <Container maxW="container.xl">
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
              <Stack spacing={4}>
                <Text
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  fontWeight="800"
                  fontSize="sm"
                  color={theme.colors.muted}
                >
                  {identitySection.eyebrow}
                </Text>
                <Heading
                  as="h2"
                  color={theme.colors.text}
                  fontFamily={theme.fontHeading}
                >
                  {identitySection.title}
                </Heading>
                <Text color={theme.colors.muted}>
                  {identitySection.description}
                </Text>
                <Text color={theme.colors.text} fontSize="sm" fontWeight="600">
                  {identitySection.trustNote}
                </Text>
              </Stack>
              <Stack spacing={3}>
                {pills.map((pill) => (
                  <Box
                    key={pill}
                    bg="white"
                    border="1px solid"
                    borderColor={theme.colors.border}
                    borderRadius="xl"
                    p={4}
                    boxShadow="0 10px 24px rgba(0,0,0,0.06)"
                  >
                    <Text color={theme.colors.text} fontWeight="700">
                      {pill}
                    </Text>
                  </Box>
                ))}
              </Stack>
            </SimpleGrid>
          </Container>
        </Box>
      );
    }

    if (slug === 'proposte-ragazzi') {
      return (
        <Box bg="#121427" px={4} py={{ base: 10, md: 14 }}>
          <Container maxW="container.xl">
            <Stack spacing={5}>
              <Text
                textTransform="uppercase"
                letterSpacing="0.08em"
                fontWeight="800"
                fontSize="sm"
                color="#fda4af"
              >
                {identitySection.eyebrow}
              </Text>
              <Heading as="h2" color="white" fontFamily={theme.fontHeading}>
                {identitySection.title}
              </Heading>
              <Text color="#e5e7eb" maxW="5xl">
                {identitySection.description}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {pills.map((pill) => (
                  <Box
                    key={pill}
                    bg="whiteAlpha.150"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius="xl"
                    p={4}
                  >
                    <Text color="white" fontWeight="700" fontSize="sm">
                      {pill}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
              <Text color="#fbcfe8" fontSize="sm">
                {identitySection.trustNote}
              </Text>
            </Stack>
          </Container>
        </Box>
      );
    }

    // proposte-famiglie (default target-friendly layout)
    return (
      <Box bg="#fff8ef" px={4} py={{ base: 10, md: 14 }}>
        <Container maxW="container.xl">
          <Stack spacing={5}>
            <Text
              textTransform="uppercase"
              letterSpacing="0.08em"
              fontWeight="800"
              fontSize="sm"
              color={theme.colors.muted}
            >
              {identitySection.eyebrow}
            </Text>
            <Heading
              as="h2"
              color={theme.colors.text}
              fontFamily={theme.fontHeading}
            >
              {identitySection.title}
            </Heading>
            <Text color={theme.colors.muted} maxW="5xl">
              {identitySection.description}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              {pills.map((pill) => (
                <Box
                  key={pill}
                  bg="white"
                  border="1px solid"
                  borderColor={theme.colors.border}
                  borderRadius="xl"
                  p={4}
                >
                  <Text
                    color={theme.colors.text}
                    fontWeight="700"
                    fontSize="sm"
                  >
                    {pill}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
            <Text color={theme.colors.text} fontSize="sm" fontWeight="600">
              {identitySection.trustNote}
            </Text>
          </Stack>
        </Container>
      </Box>
    );
  };

  const renderAdultStrategicPhoto = (slot) => {
    if (slug !== 'eventi-adulti') return null;
    if (slot === 'postIdentity') {
      return (
        <Box bg="#f7fafc" px={4} py={{ base: 8, md: 10 }}>
          <Container maxW="container.xl">
            <Box borderRadius="2xl" overflow="hidden" boxShadow="lg">
              <Image
                src={laSedePhoto}
                alt="Spazi CIL per eventi adulti"
                w="100%"
                h={{ base: '240px', md: '360px' }}
                objectFit="cover"
              />
            </Box>
          </Container>
        </Box>
      );
    }
    if (slot === 'midContent') {
      return (
        <Box bg={theme.colors.bg} px={4} py={{ base: 8, md: 10 }}>
          <Container maxW="container.xl">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <Box borderRadius="2xl" overflow="hidden" boxShadow="md">
                <Image
                  src={heroImage?.src}
                  alt="Evento"
                  w="100%"
                  h={{ base: '220px', md: '260px' }}
                  objectFit="cover"
                />
              </Box>
              <Box borderRadius="2xl" overflow="hidden" boxShadow="md">
                <Image
                  src={homeTwoPhoto}
                  alt="Momenti sociali CIL"
                  w="100%"
                  h={{ base: '220px', md: '260px' }}
                  objectFit="cover"
                />
              </Box>
            </SimpleGrid>
          </Container>
        </Box>
      );
    }
    if (slot === 'preFinalCta') {
      return (
        <Box bg="white" px={4} py={{ base: 8, md: 10 }}>
          <Container maxW="container.xl">
            <Box borderRadius="2xl" overflow="hidden" boxShadow="lg">
              <Image
                src={rinnovoTesseraBgPhoto}
                alt="Atmosfera eventi per adulti"
                w="100%"
                h={{ base: '220px', md: '320px' }}
                objectFit="cover"
              />
            </Box>
          </Container>
        </Box>
      );
    }
    return null;
  };

  const renderFamilyStrategicPhoto = (slot) => {
    if (slug !== 'proposte-famiglie') return null;
    if (slot === 'postIdentity') {
      return (
        <Box bg="#fff8ef" px={4} py={{ base: 8, md: 10 }}>
          <Container maxW="container.xl">
            <Box borderRadius="2xl" overflow="hidden" boxShadow="md">
              <Image
                src={ce2024Photo}
                alt="Esperienze CIL per famiglie"
                w="100%"
                h={{ base: '240px', md: '340px' }}
                objectFit="cover"
              />
            </Box>
          </Container>
        </Box>
      );
    }
    if (slot === 'midContent') {
      return (
        <Box bg={theme.colors.bg} px={4} py={{ base: 8, md: 10 }}>
          <Container maxW="container.xl">
            <Box borderRadius="2xl" overflow="hidden" boxShadow="md">
              <Image
                src={piantalaPhoto}
                alt="Attivita family-friendly CIL"
                w="100%"
                h={{ base: '220px', md: '300px' }}
                objectFit="cover"
              />
            </Box>
          </Container>
        </Box>
      );
    }
    if (slot === 'preFinalCta') {
      return (
        <Box bg="white" px={4} py={{ base: 8, md: 10 }}>
          <Container maxW="container.xl">
            <Box borderRadius="2xl" overflow="hidden" boxShadow="lg">
              <Image
                src={laSedePhoto}
                alt="Spazio CIL per proposte famiglie"
                w="100%"
                h={{ base: '220px', md: '320px' }}
                objectFit="cover"
              />
            </Box>
          </Container>
        </Box>
      );
    }
    return null;
  };

  return (
    <>
      <Helmet>
        <title>{title} - CIL - Creatività in Libertà</title>
        <meta property="og:title" content={title} />
        <meta property="og:description" content={subtitle} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/${slug}`} />
        <link rel="stylesheet" href={getThemeFontImport(theme.key)} />
      </Helmet>

      <StandaloneHero
        theme={theme}
        eyebrow={typedEyebrow}
        title={title}
        subtitle={subtitle}
        primaryCta={heroPrimaryCta}
        secondaryCta={heroSecondaryCta}
        heroImage={heroImage}
      />

      {renderIdentitySection()}
      {slug === 'magic-tcg' && hasEvents ? (
        <DynamicEventsSection
          config={dynamicContent}
          sectionId={`${slug}-eventi`}
        />
      ) : null}
      {renderMagicPhotoMoments()}
      {renderAdultStrategicPhoto('postIdentity')}
      {renderFamilyStrategicPhoto('postIdentity')}

      {needsBasedLayout ? (
        <>
          <LandingNeedsSection
            theme={theme}
            title={sections.needsTitle}
            intro={sections.intro}
            needs={needs}
            sectionId={`${slug}-contenuti`}
          />
          <LandingCategoryIdeasSection
            theme={theme}
            title={sections.categoriesTitle}
            categories={categories}
          />
          {renderAdultStrategicPhoto('midContent')}
          {renderFamilyStrategicPhoto('midContent')}
        </>
      ) : (
        <>
          <Box
            id={`${slug}-contenuti`}
            scrollMarginTop="200px"
            bg={theme.colors.bg}
            px={4}
            py={{ base: 10, md: 14 }}
          >
            <Container maxW="container.xl">
              <Stack spacing={4}>
                <Text
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  fontWeight="800"
                  fontSize={slug === 'magic-tcg' ? 'md' : 'sm'}
                  color={theme.colors.muted}
                  fontFamily={theme.fontBody}
                >
                  {sections.audienceTitle}
                </Text>
                <Heading
                  as="h2"
                  fontSize={{ base: '2xl', md: '4xl' }}
                  color={theme.colors.text}
                  fontFamily={theme.fontHeading}
                  maxW="4xl"
                >
                  {target}
                </Heading>
              </Stack>
            </Container>
          </Box>
          {offers.length && slug === 'magic-tcg' ? (
            <Box bg={theme.colors.bg} py={{ base: 10, md: 14 }} px={4}>
              <Container maxW="container.xl">
                <Stack spacing={6}>
                  {sections.offersTitle ? (
                    <Text
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      fontWeight="800"
                      fontSize="md"
                      color={theme.colors.muted}
                      fontFamily={theme.fontBody}
                    >
                      {sections.offersTitle}
                    </Text>
                  ) : null}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    {offers.map((item) => (
                      <Box
                        key={item}
                        bg={theme.colors.surface}
                        borderRadius="2xl"
                        p={6}
                        border="1px solid"
                        borderColor={theme.colors.border}
                        boxShadow="0 10px 24px rgba(0, 0, 0, 0.08)"
                        transform={{ base: 'none', md: 'translateY(8px)' }}
                        transition="transform 220ms ease, box-shadow 220ms ease"
                        _hover={{
                          transform: { base: 'none', md: 'translateY(-4px)' },
                          boxShadow: '0 14px 28px rgba(0, 0, 0, 0.12)',
                        }}
                      >
                        <Text
                          fontFamily={theme.fontBody}
                          color={theme.colors.text}
                          fontSize="md"
                          fontWeight="700"
                        >
                          {item}
                        </Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Stack>
              </Container>
            </Box>
          ) : offers.length ? (
            <StandaloneFeatureGrid
              theme={theme}
              title={sections.offersTitle}
              items={offers}
            />
          ) : null}
          {renderAdultStrategicPhoto('midContent')}
          {renderFamilyStrategicPhoto('midContent')}
        </>
      )}

      <DynamicPropostePubblicheSection config={dynamicContent} />

      {valueItems.length ? (
        <StandaloneFeatureGrid
          theme={theme}
          title={sections.valueTitle}
          items={valueItems}
        />
      ) : null}

      {renderAdultStrategicPhoto('preFinalCta')}
      {renderFamilyStrategicPhoto('preFinalCta')}

      {slug !== 'magic-tcg' ? (
        <DynamicEventsSection
          config={dynamicContent}
          sectionId={hasEvents ? `${slug}-eventi` : undefined}
        />
      ) : null}

      <StandaloneFinalCta
        theme={theme}
        title={sections.finalCtaTitle}
        text={sections.finalCtaText}
        primaryCta={landingPrimaryCta}
        secondaryCta={landingSecondaryCta}
      />
    </>
  );
}
